import * as React from 'react'
import {Badge, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {observer} from 'mobx-react-lite'
import {useDateTimezoneState} from '@oc/shell-core'
import {ClockIcon, IconByKey, PlantIcon} from '@oc/icons'
import {useOcAppBarState} from '../../Contexts/OcAppBarStateContext'
import {messages} from '../translations'

const useAppBarTimeStyles = makeOcStyles(theme => ({
    menuItem: {
        paddingLeft: theme.spacing(1),
        color: theme.syskronPalette.colors.P700
    },
    whiteColor: {
        color: theme.syskronPalette.colors.P50
    },
    blueColor: {
        color: theme.syskronPalette.colors.P700
    },
    clockDiv: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    bottomTimeDetail: {
        paddingTop: 2
    },
    upperTimeDetail: {
        paddingTop: 3
    }
}))

interface OcAppBarTimezoneMenuProps {
    anchorEl: Element | null
    customerTimeZone: boolean
    handleClose: () => void
    disableButton: boolean
}

function _OcAppBarTimezoneMenu(props: OcAppBarTimezoneMenuProps) {
    const {anchorEl, customerTimeZone, handleClose, disableButton} = props
    const {classes} = useAppBarTimeStyles()
    const dateTimezoneState = useDateTimezoneState()

    return (
        <Menu
            id="oc-app-bar-settings-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
            }}
        >
            <MenuItem
                data-cy={`app-bar-timezone-menu-${customerTimeZone ? 'customer' : 'local'}-item`}
                onClick={() => {
                    dateTimezoneState.customerTimezoneSelected = !customerTimeZone
                    handleClose()
                }}
                disabled={disableButton}
            >
                <ListItemIcon>
                    {customerTimeZone ? (
                        <IconByKey iconKey="personnel" className={classes.blueColor} />
                    ) : (
                        <PlantIcon className={classes.blueColor} />
                    )}
                </ListItemIcon>
                <ListItemText>
                    <Typography className={classes.menuItem} variant="body1">
                        {customerTimeZone
                            ? messages.getText('appBar', 'showInLocalTimezone')
                            : messages.getText('appBar', 'showInCustomerTimezone')}
                    </Typography>
                </ListItemText>
            </MenuItem>
        </Menu>
    )
}
const OcAppBarTimezoneMenu = observer(_OcAppBarTimezoneMenu)

function _OcAppBarTimezoneMenuWithTime() {
    const {classes, cx} = useAppBarTimeStyles()
    const state = useOcAppBarState()
    const dateTimezoneState = useDateTimezoneState()

    const [timezoneMenuAnchorEl, setTimezoneMenuAnchorEl] = React.useState<Element | null>(null)

    const handleOpenTimezoneMenu = React.useCallback(
        (event: React.MouseEvent) => {
            setTimezoneMenuAnchorEl(event.currentTarget)
        },
        [setTimezoneMenuAnchorEl]
    )
    const handleCloseTimezoneMenu = () => setTimezoneMenuAnchorEl(null)

    return (
        <>
            <IconButton
                data-cy="change-timezone-settings-icon-button"
                onClick={state.s2aAccount ? handleOpenTimezoneMenu : undefined}
                title={messages.getText('appBar', 'changeTimezone')}
                size="large"
            >
                {dateTimezoneState.customerTimezoneSelected && state.s2aAccount ? (
                    <Badge
                        badgeContent={<PlantIcon fontSize="inherit" className={classes.whiteColor} />}
                        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                        data-cy="customer-timezone-plant-icon"
                    >
                        <ClockIcon size="32px" className={classes.whiteColor} />
                    </Badge>
                ) : (
                    <Badge
                        badgeContent={
                            <IconByKey iconKey="personnel" fontSize="inherit" className={classes.whiteColor} />
                        }
                        data-cy="local-timezone-account-circle-icon"
                    >
                        <ClockIcon size="32px" className={classes.whiteColor} />
                    </Badge>
                )}
            </IconButton>
            <OcAppBarTimezoneMenu
                anchorEl={timezoneMenuAnchorEl}
                customerTimeZone={dateTimezoneState.customerTimezoneSelected}
                handleClose={handleCloseTimezoneMenu}
                disableButton={!state.s2aAccount}
            />
            <div className={classes.clockDiv}>
                <Tooltip title={state.localTimezoneTitle}>
                    <Typography
                        variant="body1"
                        color="textPrimary"
                        className={cx(classes.upperTimeDetail, classes.whiteColor)}
                        data-cy="local-current-time"
                    >
                        {!dateTimezoneState.customerTimezoneSelected || !state.s2aAccount ? (
                            <strong data-cy="local-current-time-strong">{state.localTimezoneTime}</strong>
                        ) : (
                            state.localTimezoneTime
                        )}
                    </Typography>
                </Tooltip>
                <Tooltip title={state.customerTimezoneTitle}>
                    <Typography
                        variant="body1"
                        color="textPrimary"
                        className={cx(classes.bottomTimeDetail, classes.whiteColor)}
                        data-cy="customer-current-time"
                    >
                        {dateTimezoneState.customerTimezoneSelected && state.s2aAccount ? (
                            <strong data-cy="customer-current-time-strong">
                                {state.s2aAccount ? state.customerTimezoneTime : '-'}
                            </strong>
                        ) : state.s2aAccount ? (
                            state.customerTimezoneTime
                        ) : (
                            '-'
                        )}
                    </Typography>
                </Tooltip>
            </div>
        </>
    )
}
export const OcAppBarTimezoneMenuWithTime = observer(_OcAppBarTimezoneMenuWithTime)
